
import { ErrorType, Tag, Trip, TripError, TripInvalidity } from '@clients/myTrips'
import IconArrowRight from '@components/icons/icon-arrow-nav-right.svg'
import FieldBasicData, { BasicDataKeyValue } from '@components/my-trips/trips-overview/FieldBasicData.vue'
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue'
import AppListItemPlaceholder from '@components/new-styleguide/AppListItemPlaceholder.vue'
import TagIcon from '@components/shared/icons/TagIcon.vue'
import TagLetterIcon from '@components/shared/icons/TagLetterIcon.vue'
import addLeadingZeros from '@util/addLeadingZeros'
import { formatTime } from '@util/dates-and-times'
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { tripToTripMutation } from '@util/tripMapper'
import { namespace } from 'vuex-class'

const invalidityTextMap = {
  [TripInvalidity.ReversedTimestamps]: 'Der Abfahrtszeitpunkt der Fahrt stimmt nicht mit dem Ankunftszeitpunkt überein',
  [TripInvalidity.ReversedOdometers]: 'Der Startkilometerstand der Fahrt stimmt nicht mit dem Endkilometerstand überein',
  [TripInvalidity.TooFast]: 'Die Dauer der Fahrt stimmt nicht mit der Strecke überein',
  [TripInvalidity.TooOld]: 'Das Start- oder Enddatum der Fahrt liegen zu weit in der Vergangenheit',
  [TripInvalidity.NegativeSpeed]: 'Die Dauer der Fahrt stimmt nicht mit der Strecke überein',
  [TripInvalidity.InvalidStartCoordinates]: 'Der Abfahrtsort der Fahrt ist fehlerhaft',
  [TripInvalidity.InvalidEndCoordinates]: 'Der Ankunftsort der Fahrt ist fehlerhaft'
}

const nextTripErrorTextMap = {
  [ErrorType.OverlapTime]: 'Der Ankunfszeitpunkt der Fahrt überschneidet sich mit dem Abfahrtszeitpunkt der nächsten Fahrt',
  [ErrorType.OverlapOdometer]: 'Der Endkilometerstand der Fahrt überschneidet sich mit dem Startkilometerstand der nächsten Fahrt',
}

const previousTripErrorTextMap = {
  [ErrorType.OverlapTime]: 'Der Abfahrtszeitpunkt der Fahrt überschneidet sich mit dem Ankunftszeitpunkt der vorherigen Fahrt',
  [ErrorType.OverlapOdometer]: 'Der Startkilometerstand der Fahrt überschneidet sich mit dem Endkilometerstand der vorherigen Fahrt',
}

const TriplogModule = namespace('Triplog');

@Component({
  name: 'TripField',
  components: {
    TagIcon,
    AppListItem,
    AppListItemPlaceholder,
    IconArrowRight,
    TagLetterIcon,
    FieldBasicData
  },
})
export default class TripField extends Vue {
  @Prop() trip: Trip

  @Prop() connection: boolean

  @Prop() noBorderTop: boolean

  @Prop() vehicleId: number

  @Prop() index: number

  @Prop({ default: [] }) nextTripErrors: Array<TripError>

  @Prop({ default: [] }) previousTripErrors: Array<TripError>

  @Prop({ default: 'myTripsEdit' }) editRoute: string

  @Prop() quickActionTags: Tag[];

  @TriplogModule.Action('updateTrip') updateTrip;

  @Prop({ default: false })
  private isLeftQuickActionsOpen: boolean;

  private loading: boolean = false;

  get value(): ListItem {
    return { title: this.getEndZone || this.getEndAddressShort, subtitle: this.getEndAddressLong,
    location: this.redirectToEdit, letterIcon: this.letterIcon, tagIcon: this.tagIcon };
  }

  get redirectToEdit() {
    return {
      name: this.editRoute,
      params: { id: this.trip.id.toString() },
    };
  }

  get tagIcon() {
    return this.trip.tag ? { value: this.trip.tag.iconName, color: this.trip.tag.color, fill: 'white' } : {};
  }

  get letterIcon() {
    return this.trip.pilot ? { value: this.trip.pilot.initial, color: this.trip.pilot.color, fill: 'white' } : null;
  }

  get tripDistance() {
    return (Math.round(this.trip.distance / 1000 * 10) / 10).toLocaleString();
  }

  get tripDuration() {
    if (this.trip.duration >= 3600000) {
      return {
        value: `${Math.floor(this.trip.duration / 3600000)}:${addLeadingZeros(Math.floor((this.trip.duration % 3600000) / 60000).toString())}`,
        key: ' h'
      };
    }
    return {
      value: `${Math.floor((this.trip.duration % 3600000) / 60000)}`,
      key: ' min'
    };
  }

  get tripTime() {
    return (this.trip.endInfo && this.trip.endInfo.timestamp)
      ? `${formatTime(new Date(this.trip.startInfo.timestamp))} - ${formatTime(new Date(this.trip.endInfo.timestamp))}`
      : `${formatTime(new Date(this.trip.startInfo.timestamp))} - `;
  }

  get basicData(): BasicDataKeyValue[] {
    return [
      { key: ' km', value: this.tripDistance },
      this.tripDuration,
      { value: this.tripTime }
    ];
  }

  get getEndZone() {
    return (this.trip.endInfo && this.trip.endInfo.zone) ? this.trip.endInfo.zone.name : null;
  }

  get getEndAddressShort() {
    return (this.trip.endInfo && this.trip.endInfo.address) ? this.trip.endInfo.address.locality : 'Unbekannt';
  }

  get getEndAddressLong() {
    return (this.trip.endInfo && this.trip.endInfo.address) ? `${this.trip.endInfo.address.streetLine}, ${this.trip.endInfo.address.areaCode}\
    ${this.trip.endInfo.address.locality}, ${this.trip.endInfo.address.stateRegion}` : 'Nicht beendet';
  }

  get invaliditiesText() {
    if (!this.trip.invalidities) {
      return [];
    }

    return this.trip.invalidities.map(invalidity => invalidityTextMap[invalidity]);
  }

  get tripErrorsText() {
    const nextTripErrorsText = this.nextTripErrors.map(te => nextTripErrorTextMap[te.type]);
    const previousTripErrorsText = this.previousTripErrors.map(te => previousTripErrorTextMap[te.type]);

    return [...nextTripErrorsText, ...previousTripErrorsText];
  }

  get isInvalid() {
    return this.invaliditiesText.length > 0 || this.tripErrorsText.length > 0;
  }

  get isMerged() {
    return this.trip['recentlyMerged'];
  }

  get topUsedTags() {
    return this.quickActionTags.slice(0, 2);
  }

  handleSwipeRight() {
    this.$emit('swipeRight', this.index);
  }

  handleSwipeLeft() {
    this.$emit('swipeLeft', this.index);
  }

  quickActionItemBackground(tag: Tag) {
    return 'background: ' + tag.color;
  }

  async assignTag(tag: Tag) {
    this.$emit('swipeLeft', this.index);
    const copy = tripToTripMutation(this.trip);
    copy.tagId = tag.id;

    this.loading = true
    await this.updateTrip({ tripId: this.trip.id, trip: copy });
    this.loading = false;
  }
}
